import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  Button,
  TextInput,
  Textarea,
  Select,
  Notification,
  NumberInput,
  Checkbox,
  Tooltip,
  Switch,
} from "@mantine/core";
import { DateInput as MantineDateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  IconFlame,
  IconRocket,
  IconEye,
  IconEyeOff,
  IconCalendar,
  IconCash,
  IconBuildingFactory2, // Added for Industry icon
  IconPaperclip,
  IconFileText,
  IconTrash,
} from "@tabler/icons-react";
import fetchApi from "../components/Lib/api";
import { cleanText } from "../components/Lib/profanityCleaner";
import { industriesData } from "../components/Onboarding/data";
import moment from "moment";
import Mixpanel from "../components/Lib/mixpanel";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mantine/hooks";
// Common Mantine input styles to reduce duplication
const commonMantineInputStyles = {
  input: {
    backgroundColor: "#f8fafc",
    border: "2px solid #e2e8f0",
    borderRadius: "12px",
    padding: "16px",
    fontSize: "16px",
    "&:focus": {
      borderColor: "#3b82f6",
      backgroundColor: "#ffffff",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  },
};

export default function PostProjectModal({ disabled = false, limitMessage = "" }) {
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [attachedFilesMeta, setAttachedFilesMeta] = useState([]); // {filename, size_bytes, content_type, file_key, checksum}
  const [uploadingFiles, setUploadingFiles] = useState([]); // {id, name, status}
  const [uploadProgress, setUploadProgress] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const stepContainerRef = useRef(null);
   const { t } = useTranslation();
  
  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleRemoveAttachedMeta = (fileKey) => {
    fetch('/uploads/delete_by_key', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
      },
      body: JSON.stringify({ file_key: fileKey }),
    })
      .then(() => {
        setAttachedFilesMeta((prev) => prev.filter((f) => f.file_key !== fileKey));
        Mixpanel.content('Project Creation File', 'Removed Pending', { file_key: fileKey });
      })
      .catch((err) => {
        console.error('Failed to delete from GCS:', err);
        // Still remove locally to avoid blocking UI; alternatively show error
        setAttachedFilesMeta((prev) => prev.filter((f) => f.file_key !== fileKey));
      });
  };

  const cleanupPendingUploads = async () => {
    const keys = attachedFilesMeta.map((m) => m.file_key).filter(Boolean);
    await Promise.all(keys.map((key) =>
      fetch('/uploads/delete_by_key', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
        },
        body: JSON.stringify({ file_key: key })
      }).catch(() => null)
    ));
    setAttachedFilesMeta([]);
    setUploadingFiles([]);
    setUploadProgress({});
  };

  // If modal is closed without successful submit, clean up orphaned uploads
  useEffect(() => {
    if (!opened && !submitted && attachedFilesMeta.length > 0) {
      cleanupPendingUploads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const form = useForm({
    initialValues: {
      name: "",
      description: "",
      industry: "technology",
      stage: "",
      roadmap: "",
      project_type: "",
      country: "",
      city: "",
      website: "",
      linkedin: "",
      social_media: "",
      public_listed: true,
      start_date: new Date(),
      open_fo_applications: true,
      end_date: null,
      details: "",
      has_budget: false,
      budget: 25.0,
    },
    validate: {
      name: (value) =>
        !value || value.trim() === "" ? "Name is required" : null,
      description: (value) =>
        !value || value.trim() === "" ? "Description is required" : null,
      industry: (value) =>
        !value || value.trim() === "" ? "Industry is required" : null, // Added validation for industry
      end_date: (value, values) => {
        if (!value) return "Project deadline is required";
        return null;
      },
      budget: (value, values) => {
        if (!values.has_budget) return null;
        if (isNaN(value) || value < 0) {
          return "Budget must be greater than $0.00";
        }
        return null;
      },
    },
  });

  useEffect(() => {
    if (stepContainerRef.current && opened) {
      const focusable = stepContainerRef.current.querySelector(
        "input, select, textarea"
      );
      if (focusable) {
        focusable.focus();
      }
    }
  }, [opened]);

  const handleSubmit = async (values) => {
    setLoading(true);
    setNotification(null);

    Mixpanel.form("Project Creation Form", "Attempted", {
      project_name: values.name,
      project_industry: values.industry,
      project_visibility: values.public_listed ? "Public" : "Private",
      project_budget: values.budget,
    });

    const startTime = performance.now();

    try {
      // Build multipart form data to support file attachments
      const fd = new FormData();
      const payload = {
          ...values,
          description: cleanText(values.description),
          details: cleanText(values.details),
        budget: values.has_budget ? values.budget : null,
      };
      Object.keys(payload).forEach((key) => {
        // Dates: serialize to ISO if present
        const val = payload[key];
        const fieldValue = val instanceof Date ? val.toISOString() : val;
        fd.append(`startup_entity[${key}]`, fieldValue == null ? "" : fieldValue);
      });
      // Send metadata for pre-uploaded files to attach server-side without re-upload
      (attachedFilesMeta || []).forEach((meta) => {
        fd.append("startup_entity[uploaded_files][]", JSON.stringify(meta));
      });

      const response = await fetchApi("/startup_entities", "POST", fd, true);

      const endTime = performance.now();
      const responseTimeMs = Math.round(endTime - startTime);

      if (response.ok) {
        const res = await response.json();
        setSubmitted(true);
        sessionStorage.setItem(
          "roadmap_payload",
          JSON.stringify(res.roadmap_payload)
        );
        setNotification({
          title: "Success!",
          message: "Project posted successfully.",
          color: "green",
        });

        Mixpanel.form("Project Creation Form", "Completed", {
          status: "success",
          project_id: res.project_id || "N/A",
          project_name: values.name,
          project_industry: values.industry,
          project_visibility: values.public_listed ? "Public" : "Private",
          project_budget: values.budget,
          response_time_ms: responseTimeMs,
        });

        setTimeout(() => {
          setOpened(false);
          form.reset();
          window.location.assign(res.redirect_path);
        }, 1500);
      } else {
        const msg =
          (await response.json()).error || "An unknown error occurred.";
        setNotification({
          title: "Submission Failed",
          message: msg,
          color: "red",
        });
        Mixpanel.form("Project Creation Form", "Failed", {
          status: "api_error",
          error_message: msg,
          http_status: response.status,
          project_name: values.name,
          project_industry: values.industry,
          response_time_ms: responseTimeMs,
        });
        setLoading(false);
      }
    } catch (error) {
      console.error("Error during submission:", error);
      setNotification({
        title: "Error",
        message: "An error occurred while posting your project.",
        color: "red",
      });

      const endTime = performance.now();
      const responseTimeMs = Math.round(endTime - startTime);

      Mixpanel.form("Project Creation Form", "Failed", {
        status: "network_error",
        error_message: error.message,
        project_name: values.name,
        project_industry: values.industry,
        response_time_ms: responseTimeMs,
      });
      setLoading(false);
    }
  };

  // ---------- File upload helpers (signed URL flow) ----------
  const calculateSHA256Checksum = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const buffer = event.target.result;
          const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const base64Checksum = btoa(String.fromCharCode.apply(null, hashArray));
          resolve(base64Checksum);
        } catch (error) {
          reject(new Error(`Failed to calculate SHA-256 checksum: ${error.message}`));
        }
      };
      reader.onerror = (error) => reject(new Error(`FileReader error: ${error.message}`));
      reader.readAsArrayBuffer(file);
    });
  };

  const uploadToGCS = (file, signedUrl, fileId) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", signedUrl, true);
      xhr.setRequestHeader("Content-Type", file.type);

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setUploadProgress((prev) => ({ ...prev, [fileId]: percent }));
        }
      });

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error("Upload failed"));
      xhr.send(file);
    });
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || []);
    for (const file of files) {
      const fileId = `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`;
      Mixpanel.custom('Project Creation File Upload Started', {
        file_name: file.name,
        file_size: file.size,
        content_type: file.type,
      });
      setUploadingFiles((prev) => [
        ...prev,
        { id: fileId, name: file.name, size: file.size, status: "preparing" },
      ]);

      try {
        setUploadingFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, status: "calculating_checksum" } : f)));
        const checksum = await calculateSHA256Checksum(file);

        setUploadingFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, status: "connecting" } : f)));
        const signedUrlRes = await fetch('/uploads/signed_url', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
          },
          body: JSON.stringify({
            filename: file.name,
            content_type: file.type,
            context_type: 'general',
          }),
        });
        if (!signedUrlRes.ok) throw new Error('Failed to get signed URL');
        const { signed_url, file_key } = await signedUrlRes.json();

        setUploadingFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, status: "uploading" } : f)));
        await uploadToGCS(file, signed_url, fileId);

        // Optionally confirm (not required for our own attach flow); we keep metadata to attach on submit
        // Remove from uploading list to avoid duplicate display
        setUploadingFiles((prev) => prev.filter((f) => f.id !== fileId));
        setUploadProgress((prev) => {
          const next = { ...prev };
          delete next[fileId];
          return next;
        });
        // Add to attached meta if not already present
        setAttachedFilesMeta((prev) => {
          if (prev.some((m) => m.file_key === file_key)) return prev;
          return [
            ...prev,
            {
              filename: file.name,
              size_bytes: file.size,
              content_type: file.type,
              file_key,
              checksum,
            },
          ];
        });
        Mixpanel.custom('Project Creation File Upload Completed', {
          file_name: file.name,
          file_size: file.size,
          content_type: file.type,
        });
      } catch (error) {
        console.error('Upload failed:', error);
        setUploadingFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, status: "error", error: error.message } : f)));
        Mixpanel.error('Project Creation File Upload Error', {
          error_message: error.message,
          error_type: 'Upload Error',
          user_action: 'Upload Project File',
        });
      }
    }
    event.target.value = '';
  };

  return (
    <>
      <Tooltip
        label={disabled ? limitMessage : t("sidebar_action.post_project")}
        withArrow
        position="right"
        disabled={!disabled}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            setOpened(true);
            form.reset();
            setNotification(null);
            Mixpanel.button('Post Project Button', {
              location: 'Main UI/Dashboard'
            });
          }}
          className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <IconRocket size={18} />
          {t("sidebar_action.post_project")}
        </button>
      </Tooltip>

      <Modal
        opened={opened}
        withCloseButton={false}
        onClose={() => setOpened(false)}
        size= {isMobile ? "100%" : "55%"}
        fullScreen={isMobile}
        radius="md"
        shadow="xl"
        centered
        overlayProps={{
          backgroundOpacity: 0.35,
          blur: 6,
        }}
        transitionProps={{ transition: "pop", duration: 200, timingFunction: "ease" }}
        styles={{
          inner: {
            padding: 0,
          },
          content: {
            height: "100vh",
            maxHeight: "100vh",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
          },
          body: {
            flex: 1,
            overflowY: "auto",
            padding: "2rem",
          },
        }}
      >
        <Modal.Body className="space-y-8" ref={stepContainerRef}>
          {notification && (
            <Notification
              title={notification.title}
              color={notification.color}
              onClose={() => setNotification(null)}
              className="mb-4"
            >
              {notification.message}
            </Notification>
          )}

          <p className="text-sm text-gray-600 -mt-2">
            Creating a project will publish a template page where people can apply. You can review applications and accept one to add them as team members and automatically close further applications.
          </p>

          <div className="space-y-3">
            <label
              htmlFor="name"
              className="block text-base font-semibold text-gray-800"
            >
             {t("post_project.name")}
            </label>
            <TextInput
              id="name"
              placeholder= {t("post_project.name_placeholder")}
              size="lg"
              className="w-full"
              styles={commonMantineInputStyles}
              {...form.getInputProps("name")}
            />
          </div>

          <div className="space-y-3">
            <label
              htmlFor="description"
              className="block text-base font-semibold text-gray-800"
            >
              {t("post_project.description")}
            </label>
            <Textarea
              id="description"
              placeholder= {t("post_project.description_placeholder")}
              minRows={5}
              maxRows={7}
              size="lg"
              className="w-full"
              styles={commonMantineInputStyles}
              {...form.getInputProps("description")}
            />
          </div>

          {/* Project Guide Details (stored in details) */}
          <div className="space-y-3">
            <label
              htmlFor="project_details"
              className="block text-base font-semibold text-gray-800"
            >
              Project guide details
            </label>
            <Textarea
              id="project_details"
              placeholder="Provide guidance, scope, and any important notes for applicants"
              minRows={5}
              maxRows={10}
              size="lg"
              className="w-full"
              styles={commonMantineInputStyles}
              {...form.getInputProps("details")}
            />
          </div>

          {/* File attachments (directly below project details) */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center">
                <IconPaperclip size={16} className="text-gray-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Attached Files</h3>
            </div>

            {attachedFilesMeta.length > 0 && (
              <div className="space-y-2">
                {attachedFilesMeta.map((f, idx) => (
                  <div key={`${f.filename}-${idx}`} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3">
                      <IconFileText size={18} className="text-green-600 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-gray-800">{f.filename}</span>
                        {typeof f.size_bytes === 'number' && (
                          <span className="text-sm text-gray-500 ml-2">({(f.size_bytes / 1024 / 1024).toFixed(1)} MB)</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <button
                        onClick={() => handleRemoveAttachedMeta(f.file_key)}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Remove file"
                        aria-label={`Remove ${f.filename}`}
                      >
                        <IconTrash size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Uploading Files Progress */}
            {uploadingFiles.length > 0 && (
              <div className="space-y-2">
                {uploadingFiles.map((uf) => (
                  <div key={uf.id} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <IconFileText size={18} className="text-blue-500 flex-shrink-0" />
                        <span className="font-medium text-gray-800">{uf.name}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {uf.status === 'error' ? (
                          <span className="text-red-600">Error</span>
                        ) : uf.status === 'completed' ? (
                          <span className="text-green-600">✓ Completed</span>
                        ) : (
                          <span className="capitalize">{uf.status}...</span>
                        )}
                      </div>
                    </div>
                    {uf.status === 'uploading' && (
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress[uf.id] || 0}%` }}></div>
                      </div>
                    )}
                    {uf.status === 'error' && (
                      <div className="text-sm text-red-600 mt-1">{uf.error || 'Upload failed'}</div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 border-gray-300 bg-gray-50 hover:border-blue-400 cursor-pointer`}>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="project-file-upload"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
              />
              <label htmlFor="project-file-upload" className="block cursor-pointer">
                <IconPaperclip size={32} className="mx-auto mb-3 text-gray-400" />
                <p className="font-semibold text-gray-700">
                  Drag & drop files here, or <span className="text-blue-600 hover:underline">browse</span>
                </p>
                <p className="text-sm text-gray-500 mt-1">Max file size: 10MB per file • Allowed: PDF, DOC, XLS, Images, TXT</p>
              </label>
            </div>
          </div>

          {/* New Industry Select Field */}
          <div className="space-y-3">
            <label
              htmlFor="industry"
              className="block text-base font-semibold text-gray-800"
            >
             {t("post_project.industry")}
            </label>
            <Select
              id="industry"
              searchable
              placeholder= {t("post_project.industry_placeholder")}
              data={industriesData} // Use the defined options
              size="lg"
              icon={
                <IconBuildingFactory2 size={18} className="text-green-500" />
              } // Using a relevant icon
              className="w-full"
              styles={commonMantineInputStyles}
              {...form.getInputProps("industry")}
            />
          </div>
          {/* End New Industry Select Field */}

          <div className="space-y-3">
            <label
              htmlFor="endDate"
              className="block text-base font-semibold text-gray-800"
            >
                 {t("post_project.deadline")}
            </label>
            <MantineDateInput
              id="endDate"
              placeholder= {t("post_project.deadline_placeholder")}
              icon={<IconCalendar size={18} className="text-green-500" />}
              size="lg"
              className="w-full"
              minDate={moment().add(7, "days").toDate()}
              styles={commonMantineInputStyles}
              {...form.getInputProps("end_date")}
            />
          </div>

          <div className="space-y-3">
            <label
              htmlFor="visibility"
              className="block text-base font-semibold text-gray-800"
            >
               {t("post_project.visibility")}
            </label>
            <Select
              id="visibility"
              data={[
                {
                  value: true,
                  label: "Public - Anyone can see and apply",
                },
                {
                  value: false,
                  label: "Private - Invite only",
                },
              ]}
              size="lg"
              icon={
                form.values.public_listed ? (
                  <IconEye size={18} className="text-green-500" />
                ) : (
                  <IconEyeOff size={18} className="text-green-500" />
                )
              }
              className="w-full"
              styles={commonMantineInputStyles}
              {...form.getInputProps("public_listed")}
            />
          </div>

           <div className="space-y-4">
             <div className="flex items-center justify-between">
             <label
                 htmlFor="has_budget"
               className="block text-base font-semibold text-gray-800"
             >
                {t("post_project.budget")}
             </label>
               <Switch
                 id="has_budget"
                 label="Enable budget"
                 checked={form.values.has_budget}
                 onChange={(event) => {
                   form.setFieldValue("has_budget", event.currentTarget.checked);
                   if (!event.currentTarget.checked) {
                     form.setFieldValue("budget", 0);
                   }
                 }}
               />
             </div>
             {form.values.has_budget && (
               <>
             <NumberInput
               id="budget"
               placeholder="25.50"
               size="lg"
               className="w-full"
               icon={<IconCash size={20} className="text-green-500" />}
               styles={commonMantineInputStyles}
               precision={2}
               step={0.01}
               min={0}
               {...form.getInputProps("budget")}
             />
             <p className="text-sm text-gray-500">
             {t("post_project.amount")}
             </p>
               </>
             )}
           </div>

          

          <div className="flex my-4 w-full justify-between gap-6">
            <div className="w-full">
              <label
                htmlFor="country"
                className="block text-base font-semibold text-gray-800"
              >
                {t("post_project.country")}
              </label>
              <TextInput
                id="country"
                placeholder="Enter country"
                size="lg"
                className="w-full"
                styles={commonMantineInputStyles}
                {...form.getInputProps("country")}
              />
            </div>

            <div className="w-full">
              <label
                htmlFor="city"
                className="block text-base font-semibold text-gray-800"
              >
                {t("post_project.city")}
              </label>
              <TextInput
                id="city"
                placeholder="Enter city"
                size="lg"
                className="w-full"
                styles={commonMantineInputStyles}
                {...form.getInputProps("city")}
              />
            </div>
          </div>

           <div className="space-y-3">
             <Checkbox
               label="Create manual roadmap"
               description="Check this if you want to create a custom roadmap for your project"
               size="md"
               styles={{
                 input: {
                   backgroundColor: "#f8fafc",
                   border: "2px solid #e2e8f0",
                 },
                 label: {
                   fontSize: "16px",
                   fontWeight: "600",
                   color: "#374151",
                 },
                 description: {
                   fontSize: "14px",
                   color: "#6b7280",
                   marginTop: "4px",
                 },
               }}
               checked={form.values.roadmap === "manual"}
               onChange={(event) => {
                 form.setFieldValue("roadmap", event.currentTarget.checked ? "manual" : "");
               }}
             />
           </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-base font-semibold text-blue-900">
                {t("post_project.publish")}
                </h4>
                <p className="text-sm text-blue-700 mt-2">
                {t("post_project.publish_subheading")}{" "}
                {form.values.public_listed ? t("post_project.public") : t("post_project.private")}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <IconFlame size={20} className="text-orange-500" />
                <span className="text-sm font-semibold text-orange-600">
                {t("post_project.ai_ready")}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <button
              onClick={async () => { await cleanupPendingUploads(); setOpened(false); }}
              className="px-6 py-3 text-gray-600 hover:text-gray-800 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              {t("post_project.cancel")}
            </button>

            <button
              onClick={form.onSubmit(handleSubmit)}
              disabled={loading || !form.isValid()}
              className={`inline-flex items-center gap-3 px-8 py-3 font-semibold rounded-lg transition-all duration-200 ${
                loading || !form.isValid()
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white shadow-lg hover:shadow-xl"
              }`}
            >
              <IconRocket size={18} />
              {loading ? t("post_project.publishing") : t("post_project.publish_button")}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
